import { useEffect, useState } from "react";
import { Link, NavLink, Route, Routes, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Ticket, Heart, UserCog, MapPin, Navigation, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, img, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLoc } from "@/lib/location";
import { fmtDate, offerText, useCountdown } from "@/lib/format";
import { Navbar, Footer } from "@/components/site/Navbar";
import { DealCard } from "@/components/site/DealCard";
import { Empty, Field, StatusBadge } from "@/components/site/bits";
import { RadiusChips } from "@/components/site/LocationPicker";

const TABS = [
  { to: "/customer", label: "My coupons", icon: Ticket, end: true, id: "coupons" },
  { to: "/customer/favorites", label: "Saved", icon: Heart, id: "favorites" },
  { to: "/customer/profile", label: "Profile", icon: UserCog, id: "profile" },
];

function CouponList() {
  const [status, setStatus] = useState("active");
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/customer/coupons", { params: { status } }).then((r) => setData(r.data));
  }, [status]);
  return (
    <div className="space-y-5">
      <div className="flex gap-2 flex-wrap">
        {["active", "redeemed", "expired", "cancelled"].map((s) => (
          <button key={s} data-testid={`coupon-tab-${s}`} onClick={() => setStatus(s)} className={`px-4 py-2 rounded-full text-sm font-bold capitalize border ${status === s ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}>
            {s} {data?.counts?.[s] != null && <span className="opacity-70">({data.counts[s]})</span>}
          </button>
        ))}
      </div>
      {!data ? <div className="h-40 bg-slate-100 rounded-2xl animate-pulse" /> : data.items.length === 0 ? (
        <Empty testid="coupons-empty" title={`No ${status} coupons`}><Link to="/deals" className="text-red-500 font-bold text-sm">Discover deals near you</Link></Empty>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.items.map((c) => (
            <Link key={c.id} to={`/customer/coupons/${c.id}`} data-testid={`coupon-card-${c.id}`} className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex hover:shadow-lg transition-shadow">
              {c.deal_image && <img src={img(c.deal_image)} alt="" className="w-28 object-cover" />}
              <div className="p-4 space-y-1 flex-1 min-w-0">
                <StatusBadge status={c.status} />
                <p className="font-heading font-bold text-slate-900 truncate">{c.deal_title}</p>
                <p className="text-xs text-slate-500 truncate">{c.merchant_name} · {c.branch_name}</p>
                <p className="text-xs text-slate-500">{c.status === "active" ? `Valid until ${fmtDate(c.deadline, c.timezone)}` : c.status === "redeemed" ? `Redeemed ${fmtDate(c.redeemed_at, c.timezone)}` : `Claimed ${fmtDate(c.claimed_at, c.timezone)}`}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function CouponView() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const cd = useCountdown(c?.deadline);
  useEffect(() => {
    const load = () => api.get(`/customer/coupons/${id}`).then((r) => setC(r.data)).catch(() => setC(false));
    load();
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
  }, [id]);
  if (c === false) return <Empty title="Coupon not found" />;
  if (!c) return <div className="h-96 bg-slate-100 rounded-3xl animate-pulse" />;
  const active = c.status === "active" && !cd.done;
  return (
    <div className="max-w-md mx-auto space-y-4">
      <Link to="/customer" className="text-sm font-semibold text-slate-600 flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> My coupons</Link>
      <div data-testid="coupon-view" className="bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 p-5 text-white">
          <p className="text-xs font-bold uppercase tracking-wider opacity-90">{c.merchant_name}</p>
          <p className="font-heading text-xl font-extrabold">{c.deal_title}</p>
          <p className="text-sm font-bold mt-1">{offerText(c)}</p>
        </div>
        <div className="p-6 flex flex-col items-center gap-4 border-b-2 border-dashed border-slate-200">
          {active ? (
            <>
              <div className="p-3 bg-white rounded-2xl border border-slate-200" data-testid="coupon-qr-code"><QRCodeSVG value={c.qr_token} size={208} level="M" /></div>
              <div className="text-center">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Redemption code</p>
                <p data-testid="coupon-code" className="font-mono text-4xl font-extrabold tracking-[0.35em] text-slate-900 pl-[0.35em]">{c.code}</p>
              </div>
              <div className="text-center"><p className="text-xs text-slate-500 mb-1">Expires in</p><span data-testid="coupon-countdown" className="font-mono font-bold text-2xl bg-slate-900 text-amber-400 px-4 py-2 rounded-xl">{cd.text}</span></div>
            </>
          ) : (
            <div className="py-8 text-center space-y-2">
              {c.status === "redeemed" ? <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" /> : <Ticket className="w-14 h-14 text-slate-300 mx-auto" />}
              <StatusBadge status={c.status === "active" ? "expired" : c.status} testid="coupon-status" />
              {c.redeemed_at && <p className="text-sm text-slate-600">Redeemed {fmtDate(c.redeemed_at, c.timezone)}</p>}
            </div>
          )}
        </div>
        <div className="p-5 space-y-2 text-sm">
          <p className="flex gap-2 text-slate-700"><MapPin className="w-4 h-4 text-red-500 shrink-0 mt-0.5" /><span><b>{c.branch_name}</b><br />{c.branch_address}</span></p>
          <p className="text-slate-500">Claimed {fmtDate(c.claimed_at, c.timezone)} · Valid until {fmtDate(c.deadline, c.timezone)}</p>
          {c.redemption_instructions && <p className="text-slate-600">{c.redemption_instructions}</p>}
          <div className="flex gap-2 pt-2">
            <a data-testid="coupon-directions" href={`https://www.google.com/maps/dir/?api=1&destination=${c.lat},${c.lng}`} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-slate-900 text-white font-semibold"><Navigation className="w-4 h-4" /> Directions</a>
            <Link to={`/deals/${c.deal_slug}`} className="flex-1 inline-flex items-center justify-center h-11 rounded-xl border border-slate-200 font-semibold">View deal</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Favorites() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/customer/favorites").then((r) => setData(r.data));
  }, []);
  if (!data) return <div className="h-40 bg-slate-100 rounded-2xl animate-pulse" />;
  if (!data.deals.length) return <Empty testid="favorites-empty" title="No saved deals yet" />;
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{data.deals.map((d, i) => <DealCard key={d.id} deal={d} index={i} />)}</div>;
}

function Profile() {
  const { user, refresh } = useAuth();
  const { loc, radius, setRadius } = useLoc();
  const [name, setName] = useState(user.name);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const save = async () => {
    try {
      await api.put("/customer/profile", { name, preferred_location: loc, preferred_radius: radius });
      await refresh();
      toast.success("Profile saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const changePw = async () => {
    try {
      await api.post("/auth/change-password", pw);
      setPw({ current_password: "", new_password: "" });
      toast.success("Password updated");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h2 className="font-heading font-bold text-lg">Profile</h2>
        <Field label="Name"><Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <p className="text-sm text-slate-600">Email: <b>{user.email}</b>{user.phone && <> · Mobile: <b>{user.phone}</b></>}</p>
        <Field label="Preferred location" hint="Change it from the location button in the top bar."><Input disabled value={loc.label} /></Field>
        <RadiusChips value={radius} onChange={setRadius} />
        <button data-testid="profile-save-button" onClick={save} className="px-6 h-11 rounded-xl bg-red-500 text-white font-bold">Save</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h2 className="font-heading font-bold text-lg">Change password</h2>
        <Field label="Current password"><Input data-testid="pw-current-input" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></Field>
        <Field label="New password"><Input data-testid="pw-new-input" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></Field>
        <button data-testid="pw-save-button" onClick={changePw} className="px-6 h-11 rounded-xl bg-slate-900 text-white font-bold">Update password</button>
      </div>
    </div>
  );
}

export default function CustomerPortal() {
  const { user, logout } = useAuth();
  return (
    <div className="bg-slate-50 min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div><p className="text-sm text-slate-500">Hi {user.name.split(" ")[0]},</p><h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Your SuperDeals</h1></div>
          <button data-testid="customer-logout" onClick={logout} className="text-sm font-semibold text-slate-500 hover:text-slate-900">Log out</button>
        </div>
        <nav className="flex gap-2 overflow-x-auto">
          {TABS.map((t) => (
            <NavLink key={t.to} to={t.to} end={t.end} data-testid={`customer-nav-${t.id}`} className={({ isActive }) => `flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap ${isActive ? "bg-red-500 text-white" : "bg-white border border-slate-200 text-slate-700"}`}>
              <t.icon className="w-4 h-4" />{t.label}
            </NavLink>
          ))}
        </nav>
        <Routes>
          <Route index element={<CouponList />} />
          <Route path="coupons/:id" element={<CouponView />} />
          <Route path="favorites" element={<Favorites />} />
          <Route path="profile" element={<Profile />} />
        </Routes>
      </div>
      <Footer />
    </div>
  );
}
